<script lang="ts">
  import { onMount } from 'svelte';
  import { internalLink } from '$lib/utils/internalLink';
  import { navigationGuards } from '$lib/state/navigationGuard';
  import { contactDetailsSnapshot, createContactDetailsGuard } from '$lib/utils/contactDetailsGuard';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { peopleApi } from '$lib/api/crm';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { formatDate } from '$lib/utils/dates';
  import { formatCompactMoney } from '$lib/utils/money';
  import { relationshipLabels, initials } from '$lib/utils/salesHome';
  import { contactDetailsBody, contactCompanyInfo, contactNextStep, moveTouchpointBody, dayOffset, nextMonday, istDay, istTime, relativeDay, shortDay, plural, sentenceCase, touchpointTypeLabel } from '$lib/utils/touchpoints';
  import { beginContactCall, consumeContactCall } from '$lib/utils/pendingCall';
  import type { Activity, Person } from '$lib/types';
  import Timeline from './Timeline.svelte';
  import Composer from './Composer.svelte';
  import ContactOwnership from './ContactOwnership.svelte';
  import CompanyCombobox from '$lib/components/ui/CompanyCombobox.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';

  let { personId }: { personId: number } = $props();
  const client = useQueryClient();
  const summary = createQuery(() => ({ queryKey: ['contact-summary', personId], queryFn: () => touchpointsApi.summary(personId) }));
  const company = $derived(summary.data?.person ? contactCompanyInfo(summary.data.person, summary.data.company) : undefined);
  const person = $derived.by(() => {
    const p = summary.data?.person;
    return p ? { ...p, company_name: p.company_name || company?.name } : undefined;
  });
  const companyAddress = $derived(Object.values(company?.address ?? {}).filter(value => typeof value === 'string' || typeof value === 'number').join(', '));
  let tab = $state('timeline'), show = $state(false), mode = $state<'log' | 'plan'>('log'), editing = $state<Activity | null>(null), initialType = $state('call');
  let editDetails = $state(false), draft = $state<Record<string, unknown>>({}), error = $state(''), busy = $state(false);
  let detailsBaseline = $state('');

  function startDetails() {
    if (!person || editDetails) return;
    draft = { ...person, custom: { ...person.custom } };
    detailsBaseline = contactDetailsSnapshot(draft);
    editDetails = true;
  }
  const detailsGuard = createContactDetailsGuard(() => ({ editing: editDetails, draft, baseline: detailsBaseline }), confirmAction, () => { editDetails = false; draft = {}; detailsBaseline = ''; });
  onMount(() => navigationGuards.register(detailsGuard));
  async function cancelDetails() {
    if (!detailsGuard.isDirty() || await detailsGuard.confirmDiscard()) detailsGuard.discardAndClose();
  }
  function newDeal() { if(person)route.push({tab:'deals'},()=>session.requestCreate('deals',{person_id:person.id,company_id:person.company_id,vertical_id:person.vertical_id})); }

  // The real next step: the earlier of the latest conversation's reminder and
  // the earliest open plan. Overdue plans count — they are never "nothing".
  const nextStep = $derived(person ? contactNextStep(person.next_followup, summary.data?.upcoming?.[0] ?? person.next_plan) : null);
  const alsoPlanned = $derived((summary.data?.upcoming ?? []).filter(plan => plan.id !== nextStep?.activity.id));
  const canMove = $derived(!!nextStep && (session.isAdmin || nextStep.activity.assignee_user_id === session.me?.user.id));
  const canComplete = $derived(!!nextStep && nextStep.kind === 'plan' && nextStep.activity.assignee_user_id === session.me?.user.id && (!nextStep.activity.scheduled_at || Date.parse(nextStep.activity.scheduled_at) <= Date.now()));
  const feedbackScores = $derived.by(() => {
    const f = summary.data?.latest_feedback;
    if (!f) return [];
    return ([['installation_score', 'Installation'], ['recommendation_score', 'Would recommend'], ['timeliness_score', 'On time & tidy']] as const)
      .filter(([key]) => typeof f[key] === 'number')
      .map(([key, label]) => ({ label, score: f[key] as number }));
  });
  const peers = $derived((summary.data?.company_people ?? []).filter(other => other.id !== personId));
  const detailLabels: Array<[string, string]> = [['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email'], ['phone', 'Phone'], ['whatsapp', 'WhatsApp'], ['job_title', 'Job title'], ['city', 'City'], ['relationship_type', 'Contact type'], ['notes', 'Notes']];
  const typeOptions = Object.entries(relationshipLabels).filter(([key]) => key !== '');

  function open(activity: Activity | null = null, plan = false, type = 'call') {
    editing = activity;
    mode = plan ? 'plan' : 'log';
    initialType = activity?.type ?? type;
    show = true;
  }
  function returned() {
    const call = consumeContactCall();
    if (!call) return;
    if (call.personId === personId) open(null, false, call.type);
    else route.push({ tab: 'people', id: call.personId });
  }
  function keyboard(e: KeyboardEvent) {
    if (show || (e.target as HTMLElement)?.closest('input,textarea,select,[contenteditable=true],[data-tym-modal]') || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.toLowerCase() === 'l') open();
    if (e.key.toLowerCase() === 's') open(null, true, 'meeting');
  }
  async function reschedule(days: number) {
    if (!nextStep || busy) return;
    busy = true;
    try {
      const date = days === 0 ? nextMonday() : dayOffset(days);
      await touchpointsApi.reschedule(nextStep.activity.id, moveTouchpointBody(nextStep.activity, date));
      await client.invalidateQueries();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      busy = false;
    }
  }
  async function saveDetails() {
    if (!person || busy) return;
    busy = true;
    try {
      await peopleApi.update(person.id, contactDetailsBody(draft));
      editDetails = false;
      await client.invalidateQueries();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      busy = false;
    }
  }
  async function remove() {
    if (!person) return;
    const s = summary.data;
    if (!await confirmAction(`Delete ${person.first_name} ${person.last_name}? Linked work: ${plural(s?.open_deals ?? 0, 'open deal')}, ${plural(s?.open_cases ?? 0, 'open case')}, ${plural(s?.conversation_count ?? 0, 'conversation')}. Historical links may remain.`)) return;
    try {
      await peopleApi.remove(person.id);
      await client.invalidateQueries();
      route.push({ tab: 'people' });
    } catch (err) {
      error = getErrorMessage(err);
    }
  }
  function planWhen(plan: Activity): string {
    const at = plan.scheduled_at ?? plan.due_at;
    return whenLabel(at ? istDay(at) : null, plan.scheduled_at ? istTime(plan.scheduled_at) : null);
  }
  function whenLabel(day: string | null, time: string | null): string {
    if (!day) return 'No date';
    const relative = relativeDay(day);
    const near = ['Today', 'Tomorrow', 'Yesterday'].includes(relative);
    const base = `${near ? relative : shortDay(day)}${time ? `, ${time}` : ''}`;
    return near ? base : `${base} · ${relative.toLowerCase()}`;
  }
</script>

<svelte:window onfocus={returned} onkeydown={keyboard} />
<div class="tym-page hub">
  <a class="day-link back" href="#/people" use:internalLink={{ tab: 'people' }}>← All contacts</a>
  {#if summary.isPending}
    <p class="tym-loading-state">Loading contact…</p>
  {:else if summary.isError}
    <p class="tym-error-state" role="alert">{getErrorMessage(summary.error)} <button type="button" class="day-mini-btn" onclick={() => summary.refetch()}>Retry</button></p>
  {:else if person}
    <header class="day-card hero">
      <div class="identity">
        <span class="avatar" aria-hidden="true">{initials(person.first_name, person.last_name)}</span>
        <div>
          <h1>{person.first_name} {person.last_name}</h1>
          <p class="day-meta">
            {person.job_title ?? ''}
            {#if person.company_id}{person.job_title ? ' at ' : ''}<a class="day-link" href={`#/companies/${person.company_id}`} use:internalLink={{ tab: 'companies', id: person.company_id }}>{person.company_name ?? person.hotel_name ?? person.brand_name ?? 'Company'}</a>{/if}
            {#if person.city}{person.job_title || person.company_id ? ' · ' : ''}{person.city}{/if}
          </p>
          <div class="chips">
            <span class="day-chip day-chip-info">{relationshipLabels[person.relationship_type ?? ''] ?? sentenceCase(person.relationship_type)}</span>
            <span class="day-chip {person.owner_user_id ? 'day-chip-ok' : 'day-chip-warn'}">Owner: {person.contact_owner_name ?? 'No owner'}</span>
            {#if summary.data?.last_contact_at}<span class="day-chip day-chip-info">Last contact {relativeDay(summary.data.last_contact_at).toLowerCase()}</span>{/if}
          </div>
        </div>
      </div>
      <div class="hero-actions">
        {#if person.phone}<a class="btn-secondary" href={`tel:${person.phone}`} onclick={() => beginContactCall(person)}>Call</a>{/if}
        {#if person.whatsapp || person.phone}<a class="btn-secondary" target="_blank" rel="noreferrer" href={`https://wa.me/${(person.whatsapp || person.phone).replace(/\D/g, '')}`} onclick={() => beginContactCall(person, 'whatsapp')}>WhatsApp</a>{/if}
        {#if person.email}<a class="btn-secondary" href={`mailto:${person.email}`}>Email</a>{/if}
        <button type="button" class="btn-secondary" onclick={() => open(null, true, 'meeting')}>Plan</button>
        <button type="button" class="btn-primary" onclick={() => open()}>Log</button>
        <details class="menu">
          <summary class="btn-secondary" aria-label="Contact actions">⋯</summary>
          <div class="menu-list">
            <button type="button" onclick={() => { tab = 'details'; startDetails(); }}>Edit details</button>
            <button type="button" disabled>Merge duplicates — coming later</button>
            <button type="button" class="danger" onclick={remove}>Delete contact</button>
          </div>
        </details>
      </div>
    </header>
    {#if error}<p role="alert" class="tym-error-state">{error}</p>{/if}

    <div class="layout">
      <main>
        <section class="day-card next" class:overdue={nextStep?.overdue} class:missing={!nextStep && !person.no_followup}>
          <p class="eyebrow">Next step</p>
          {#if nextStep}
            {@const a = nextStep.activity}
            <div class="next-body">
              <div>
                <h2>{nextStep.title}</h2>
                <p class="day-meta">
                  <strong class:late={nextStep.overdue}>{nextStep.overdue ? 'Overdue · ' : ''}{whenLabel(nextStep.day, nextStep.time)}</strong>
                  · {nextStep.kind === 'plan' ? touchpointTypeLabel(a.type) : 'Follow-up'}
                  · {nextStep.kind === 'plan' ? "Who's going" : 'For'}: {a.assignee_name ?? (a.assignee_user_id ? 'Former team member' : 'Unassigned')}
                  {#if a.location}{' · '}{a.location}{/if}
                </p>
              </div>
              <div class="row-actions">
                {#if nextStep.kind === 'plan'}
                  {#if canComplete}<button type="button" class="btn-primary" onclick={() => open(a)}>Done — log outcome</button>{/if}
                {:else}
                  <button type="button" class="btn-primary" onclick={() => open()}>Done — log outcome</button>
                {/if}
                {#if canMove}
                  <button type="button" class="day-mini-btn" disabled={busy} onclick={() => reschedule(1)}>Tomorrow</button>
                  <button type="button" class="day-mini-btn" disabled={busy} onclick={() => reschedule(3)}>In 3 days</button>
                  <button type="button" class="day-mini-btn" disabled={busy} onclick={() => reschedule(0)}>Next Monday</button>
                {/if}
                <button type="button" class="day-mini-btn" onclick={() => open(a, nextStep.kind === 'plan')}>{nextStep.kind === 'plan' ? 'Pick a time / edit' : 'Change'}</button>
              </div>
            </div>
          {:else if person.no_followup}
            <div class="next-body">
              <p>No follow-up needed — recorded on the last conversation.</p>
              <button type="button" class="day-mini-btn" onclick={() => open(null, true, 'call')}>Plan something anyway</button>
            </div>
          {:else}
            <div class="next-body">
              <div>
                <h2>No next step yet</h2>
                <p class="day-meta">Log the last conversation with a next step, or plan the next one.</p>
              </div>
              <div class="row-actions">
                <button type="button" class="btn-primary" onclick={() => open()}>Log and set next step</button>
                <button type="button" class="btn-secondary" onclick={() => open(null, true, 'meeting')}>Plan</button>
              </div>
            </div>
          {/if}
          {#if alsoPlanned.length}
            <div class="also">
              <p class="eyebrow">Also planned</p>
              {#each alsoPlanned as plan (plan.id)}
                <button type="button" class="day-row plan-row" onclick={() => open(plan, true)}>
                  <span class="when">{planWhen(plan)}</span>
                  <span class="what"><strong>{plan.title || touchpointTypeLabel(plan.type)}</strong> · {touchpointTypeLabel(plan.type)} · {plan.assignee_name ?? 'Unassigned'}{#if plan.location}{' · '}{plan.location}{/if}</span>
                </button>
              {/each}
            </div>
          {/if}
        </section>

        <div class="day-segment scroll tabs" role="group" aria-label="Contact sections">
          {#each [['timeline', 'Timeline'], ['deals', `Deals · ${summary.data?.open_deals ?? 0} open`], ['cases', `Cases · ${summary.data?.open_cases ?? 0} open`], ['details', 'Details']] as [key, label]}
            <button type="button" aria-pressed={tab === key} onclick={() => tab = key}>{label}</button>
          {/each}
        </div>

        {#if tab === 'timeline'}
          <Timeline {personId} verticalId={person.vertical_id} companyId={person.company_id} />
        {:else if tab === 'deals'}
          <div class="row-actions"><button type="button" class="btn-secondary" onclick={newDeal}>＋ New deal</button></div>
          <div class="cards">
            {#each summary.data?.deals ?? [] as deal (deal.id)}
              <a class="day-card item" href={`#/deals/${deal.id}`} use:internalLink={{ tab: 'deals', id: deal.id }}>
                <div><strong>{deal.name}</strong><span class="day-meta">Deal #{deal.id}{#if deal.job_status}{' · '}Job: {sentenceCase(deal.job_status)}{/if}</span></div>
                <div class="item-side"><span class="day-chip day-chip-info">{deal.stage}</span><strong class="amount">{formatCompactMoney(deal.amount, deal.currency)}</strong></div>
              </a>
            {:else}<p class="tym-empty-state">No deals linked to this contact yet.</p>{/each}
          </div>
        {:else if tab === 'cases'}
          <div class="cards">
            {#each [...(summary.data?.cases ?? [])].sort((a, b) => Number(a.status === 'resolved') - Number(b.status === 'resolved')) as issue (issue.id)}
              <a class="day-card item" href={`#/issues/${issue.id}`} use:internalLink={{ tab: 'issues', id: issue.id }}>
                <div><strong>{issue.title}</strong><span class="day-meta">Case #{issue.id}</span></div>
                <div class="item-side">
                  <span class="day-chip {issue.status === 'resolved' ? 'day-chip-ok' : 'day-chip-warn'}">{sentenceCase(issue.status)}</span>
                  {#if issue.customer_satisfaction === 'pending'}<span class="day-chip day-chip-danger">Call to confirm</span>{/if}
                </div>
              </a>
            {:else}<p class="tym-empty-state">No cases linked to this contact.</p>{/each}
          </div>
        {:else}
          <section class="day-card details">
            <div class="section-head">
              <h2 class="day-title">Contact details</h2>
              {#if !editDetails}<button type="button" class="day-mini-btn" onclick={startDetails}>Edit details</button>{/if}
            </div>
            <div class="detail-grid">
              {#each detailLabels as [key, label]}
                <label class="field" class:wide={key === 'notes'}>{label}
                  {#if editDetails}
                    {#if key === 'relationship_type'}
                      <select class="input" bind:value={draft.relationship_type}>
                        {#each typeOptions as [value, text]}<option {value}>{text}</option>{/each}
                        {#if draft.relationship_type && !relationshipLabels[String(draft.relationship_type)]}<option value={draft.relationship_type}>{sentenceCase(String(draft.relationship_type))}</option>{/if}
                      </select>
                    {:else if key === 'notes'}
                      <textarea class="input" rows="3" bind:value={draft.notes}></textarea>
                    {:else}
                      <input class="input" bind:value={draft[key]} />
                    {/if}
                  {:else}
                    <span class="value">{key === 'relationship_type' ? relationshipLabels[person.relationship_type ?? ''] ?? sentenceCase(person.relationship_type) : String(person[key as keyof Person] || '—')}</span>
                  {/if}
                </label>
              {/each}
              {#if editDetails}
                {#if session.me?.verticals.find(v => v.id === person.vertical_id)?.slug === 'hotels'}
                  <label class="field">Hotel<CompanyCombobox verticalId={person.vertical_id} kind="hotel" bind:value={draft.hotel_company_id as number | undefined} /></label>
                  <label class="field">Brand<CompanyCombobox verticalId={person.vertical_id} kind="brand" bind:value={draft.brand_company_id as number | undefined} /></label>
                  <label class="field">Group<CompanyCombobox verticalId={person.vertical_id} kind="owner" bind:value={draft.owner_company_id as number | undefined} /></label>
                {:else}
                  <label class="field">Company<CompanyCombobox verticalId={person.vertical_id} bind:value={draft.company_id as number | undefined} /></label>
                {/if}
              {:else}
                <div class="field">Company<span class="value">{person.company_name ?? 'Not linked'}</span></div>
                {#if person.hotel_name || person.brand_name || person.owner_name}<div class="field">Hotel · brand · group<span class="value">{[person.hotel_name, person.brand_name, person.owner_name].filter(Boolean).join(' · ')}</span></div>{/if}
                {#each session.customFieldsFor('people', person.vertical_id) as field}<div class="field">{field.label}<span class="value">{String(person.custom[field.key] ?? '—')}</span></div>{/each}
              {/if}
            </div>
            {#if editDetails}
              <CustomFieldsForm defs={session.customFieldsFor('people', person.vertical_id)} bind:values={draft.custom as Record<string, unknown>} />
              <div class="row-actions">
                <button type="button" class="btn-primary" disabled={busy} onclick={saveDetails}>Save details</button>
                <button type="button" class="btn-secondary" disabled={busy} onclick={cancelDetails}>Cancel editing</button>
              </div>
            {/if}
          </section>
        {/if}
      </main>

      <aside>
        <section class="day-card about">
          <h3 class="day-title small">Reach</h3>
          {#each [['Phone', person.phone], ['WhatsApp', person.whatsapp], ['Email', person.email]] as [label, value]}
            {#if value}
              <div class="fact"><span>{label}</span><strong>{value}</strong><button type="button" class="copy" aria-label={`Copy ${label}`} onclick={() => navigator.clipboard?.writeText(value)}>Copy</button></div>
            {/if}
          {/each}
          {#if !person.phone && !person.whatsapp && !person.email}<p class="day-meta">No phone or email recorded. <button type="button" class="day-link" onclick={() => { tab = 'details'; startDetails(); }}>Add one</button></p>{/if}
        </section>

        <section class="day-card about">
          <h3 class="day-title small">Relationship</h3>
          <div class="fact"><span>First contact</span><strong>{summary.data?.first_contact_at ? `${formatDate(summary.data.first_contact_at.slice(0, 10))}` : '—'}</strong></div>
          <div class="fact"><span>Last contact</span><strong>{summary.data?.last_contact_at ? relativeDay(summary.data.last_contact_at) : 'Never'}</strong></div>
          <div class="fact"><span>Conversations</span><strong>{summary.data?.conversation_count ?? 0}</strong></div>
          <div class="fact"><span>Lead source</span><strong>{summary.data?.lead_source || '—'}</strong></div>
        </section>

        {#if person.company_id}
          <section class="day-card about">
            <h3 class="day-title small">Company</h3>
            <a class="day-link" href={`#/companies/${person.company_id}`} use:internalLink={{ tab: 'companies', id: person.company_id }}>{person.company_name || 'Open company'}</a>
            {#if companyAddress || company?.city}<p class="day-meta">{companyAddress || company?.city}</p>{/if}
            {#if peers.length}
              <p class="eyebrow">Others here</p>
              {#each peers as other (other.id)}<a class="day-link" href={`#/people/${other.id}`} use:internalLink={{ tab: 'people', id: other.id }}>{other.first_name} {other.last_name}</a>{/each}
            {/if}
          </section>
        {/if}

        {#if summary.data?.latest_feedback}
          <section class="day-card about">
            <h3 class="day-title small">Latest job feedback</h3>
            {#if typeof summary.data.latest_feedback.submitted_at === 'string'}<p class="day-meta">Submitted {relativeDay(summary.data.latest_feedback.submitted_at).toLowerCase()}</p>{/if}
            {#each feedbackScores as row}
              <div class="fact"><span>{row.label}</span><strong class:low={row.score <= 2}>{row.score}/5</strong></div>
            {:else}<p class="day-meta">Scores not recorded.</p>{/each}
            {#if typeof summary.data.latest_feedback.deal_id === 'number'}<a class="day-link" href={`#/deals/${summary.data.latest_feedback.deal_id}`} use:internalLink={{ tab: 'deals', id: summary.data.latest_feedback.deal_id as number }}>Open the deal ↗</a>{/if}
          </section>
        {/if}

        <ContactOwnership {person} onTransferred={() => route.replace({ tab: 'people' })} />
      </aside>
    </div>
  {/if}
</div>
{#if show && person}<Composer {person} activity={editing} {mode} {initialType} onClose={() => show = false} />{/if}

<style>
  .hub { display: grid; gap: 18px; max-width: 1280px; }
  .back { justify-self: start; }
  .hero { display: flex; justify-content: space-between; align-items: center; gap: 18px; flex-wrap: wrap; }
  .identity { display: flex; gap: 16px; align-items: center; min-width: 0; }
  .avatar { flex: none; display: inline-flex; width: 56px; height: 56px; align-items: center; justify-content: center; border-radius: 50%; background: var(--tym-navy); color: var(--tym-lime); font-family: var(--tym-font-display); font-weight: 700; font-size: 20px; }
  h1 { font-family: var(--tym-font-display); font-size: 26px; font-weight: 700; line-height: 1.15; letter-spacing: -.01em; color: var(--tym-navy); }
  h2 { font-family: var(--tym-font-display); font-size: 18px; font-weight: 700; color: var(--tym-navy); }
  .chips, .row-actions, .hero-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
  .chips { margin-top: 8px; }
  .eyebrow { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--tym-teal); }
  .menu { position: relative; }
  .menu summary { list-style: none; cursor: pointer; }
  .menu summary::-webkit-details-marker { display: none; }
  .menu-list { position: absolute; right: 0; top: calc(100% + 6px); z-index: 10; display: grid; min-width: 220px; padding: 6px; border-radius: var(--tym-radius-md); background: var(--tym-white); box-shadow: var(--tym-shadow-3); }
  .menu-list button { text-align: left; padding: 10px 12px; border-radius: var(--tym-radius-sm); font-size: 13px; color: var(--tym-navy); }
  .menu-list button:hover:not(:disabled) { background: var(--tym-light-carbon); }
  .menu-list button:disabled { color: var(--tym-dark-gray); }
  .menu-list .danger { color: var(--tym-danger); }
  .layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; }
  main, aside { min-width: 0; display: grid; gap: 14px; }
  .next { display: grid; gap: 10px; border-left: 5px solid var(--tym-teal); }
  .next.overdue { border-left-color: var(--tym-danger); }
  .next.missing { border-left-color: var(--tym-warning); background: #fffaf0; }
  .next-body { display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; }
  .late { color: var(--tym-danger); }
  .also { display: grid; gap: 2px; margin-top: 4px; }
  .plan-row { width: 100%; text-align: left; font-size: 13px; }
  .plan-row .when { flex: none; width: 190px; color: var(--tym-dark-gray); font-size: 12px; }
  .plan-row strong { color: var(--tym-navy); }
  .tabs { justify-self: start; }
  .cards { display: grid; gap: 10px; }
  .item { display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; padding: 16px 18px; }
  .item:hover { box-shadow: var(--tym-shadow-2); }
  .item > div:first-child { display: grid; gap: 3px; }
  .item strong { color: var(--tym-navy); }
  .item-side { display: flex; align-items: center; gap: 10px; }
  .amount { font-family: var(--tym-font-display); }
  .details { display: grid; gap: 16px; }
  .section-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
  .detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 18px; }
  .field { display: grid; gap: 5px; font-size: 12px; font-weight: 600; color: var(--tym-dark-gray); }
  .field.wide { grid-column: 1 / -1; }
  .value { font-size: 14px; font-weight: 500; color: var(--tym-heavy-carbon); white-space: pre-wrap; }
  .about { display: grid; gap: 10px; padding: 18px 20px; }
  .day-title.small { font-size: 15px; }
  .fact { display: flex; align-items: baseline; gap: 8px; font-size: 13px; }
  .fact span { flex: none; width: 104px; color: var(--tym-dark-gray); }
  .fact strong { min-width: 0; flex: 1; overflow-wrap: anywhere; font-weight: 600; color: var(--tym-navy); }
  .fact .low { color: var(--tym-danger); }
  .copy { font-size: 12px; color: var(--tym-teal); min-height: 32px; }
  @media (max-width: 1000px) {
    .layout { grid-template-columns: 1fr; }
  }
  @media (max-width: 640px) {
    .hero-actions { width: 100%; position: sticky; top: 0; z-index: 2; background: var(--tym-white); padding: 6px 0; }
    .detail-grid { grid-template-columns: 1fr; }
    .plan-row { flex-wrap: wrap; }
    .plan-row .when { width: 100%; }
    h1 { font-size: 22px; }
  }
</style>
